import { useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNotificationStore } from "@/stores/notification-store";
import { NotificationPanelProps } from "./types";
import { NotificationItem } from "./notification-item";
import { NotificationTabs } from "./notification-tabs";

export function NotificationPanel({
  notifications,
  apps,
  onMarkAsRead,
  onMarkAllAsRead,
  onViewAll,
  onCategoryChange,
  className,
}: NotificationPanelProps) {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const { unreadCount } = useNotificationStore();

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    onCategoryChange?.(category);
  };

  return (
    <div
      className={cn(
        "aero:bg-white aero:border aero:border-neutral-300 aero:rounded-[10px]",
        "aero:p-4 aero:flex aero:flex-col aero:gap-3",
        "aero:relative",
        className,
      )}
    >
      {/* Pointing arrow at top */}
      <div className="aero:absolute aero:-top-[9px] aero:right-[7px]">
        <svg
          width="20"
          height="10"
          viewBox="0 0 20 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M10 0L20 10H0L10 0Z" fill="white" />
          <path d="M10 0L0 10" stroke="#d4d4d8" strokeWidth="1" />
          <path d="M10 0L20 10" stroke="#d4d4d8" strokeWidth="1" />
        </svg>
      </div>

      {/* Header */}
      <div className="aero:flex aero:gap-[27px] aero:items-center aero:w-full">
        <div className="aero:flex-1">
          <h3 className="aero:text-lg aero:font-bold aero:leading-7 aero:text-foreground">
            Notification{" "}
            <span className="aero:text-blue-600">({unreadCount})</span>
          </h3>
        </div>

        {onMarkAllAsRead && (
          <button
            onClick={onMarkAllAsRead}
            className={cn(
              "aero:flex aero:gap-2 aero:items-center aero:justify-center",
              "aero:px-3 aero:py-2 aero:h-9 aero:rounded",
              "aero:hover:bg-neutral-50 aero:transition-colors aero:cursor-pointer",
            )}
          >
            <Check className="aero:size-4 aero:text-blue-600" />
            <span className="aero:text-sm aero:font-bold aero:leading-5 aero:text-blue-800">
              Mark all as read
            </span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <NotificationTabs
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
        apps={apps}
      />

      {/* Notification List - Max 5 visible with scroll */}
      <div className="aero:flex aero:flex-col aero:gap-2 aero:max-h-[525px] aero:overflow-y-auto aero:pr-1">
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={onMarkAsRead}
              apps={apps}
            />
          ))
        ) : (
          <div className="aero:text-center aero:py-8 aero:text-neutral-500 aero:text-sm">
            No notifications in this category
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="aero:h-px aero:w-full aero:bg-neutral-300" />

      {/* Footer */}
      {onViewAll && (
        <button
          onClick={onViewAll}
          className="aero:text-blue-600 aero:font-semibold aero:text-base aero:leading-7 aero:hover:text-blue-700 aero:transition-colors aero:text-left aero:cursor-pointer"
        >
          View all notifications
        </button>
      )}
    </div>
  );
}
